import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton, ArrowLink } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section, Pill } from "@/components/site/pageparts";
import { CopyInstall, PypiStats } from "@/components/site/CopyInstall";
import { IngestionDiagram, RetrievalFusionDiagram, MemoryGraphDiagram } from "@/components/site/Diagrams";

const CodeBlock = ({ children }) => (
  <pre className="overflow-x-auto border border-white/10 bg-white/[0.02] p-6 font-mono text-[13px] leading-relaxed text-bone/90">
    <code>{children}</code>
  </pre>
);

const ARCH = [
  { t: "On-device embeddings", d: "sentence-transformers (MiniLM) compute vectors locally — no corpus is sent out to embed." },
  { t: "Vector + metadata store", d: "Vectors in an on-disk store (LanceDB); metadata and source-of-truth in local SQLite." },
  { t: "Content-addressed chunks", d: "doc_id = hash(content), chunk_id = hash(chunk, config). Same input, same memory state." },
  { t: "Multi-signal retrieval", d: "Semantic (vectors) + temporal (recency) + relational (a local knowledge graph), fused and ranked." },
  { t: "Optional, swappable reasoning", d: "A local model (Ollama) answers offline; a remote model is an accelerator, never a requirement." },
  { t: "Consent-gated capture", d: "Ambient capture routes through an approval inbox before anything enters durable memory." },
];

export default function UPII() {
  return (
    <div data-testid="upii-page">
      <Seo title="UPII — Unified Personal Intelligence Infrastructure" description="Local-first, sovereign memory for individuals. Open standard and reference implementation, live on PyPI." />
      <PageHero
        eyebrow="Research · UPII"
        title="Unified Personal Intelligence Infrastructure"
        lead="An open standard and reference implementation for personal memory: a single, persistent, owner-controlled context graph that sits beneath every model, agent and application you use."
      >
        <CTAButton href="https://pypi.org/project/upii/" external variant="solid">
          View on PyPI <ArrowUpRight className="h-4 w-4" />
        </CTAButton>
        <Pill tone="signal">MIT · Python 3.10–3.12</Pill>
      </PageHero>

      <Section>
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>The idea</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-[1.08] tracking-tighter text-bone md:text-4xl">
              Personal context is a primitive, not a feature.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-mist">
              Today's stack has models, agents and connectors — but no memory-and-identity substrate that outlives any
              single app and that a person actually controls. UPII is that missing layer. Fire any model without losing
              yourself.
            </p>
            <div className="mt-8">
              <ArrowLink to="/research/memory-problem">Read the manifesto</ArrowLink>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <RetrievalFusionDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 1 — The Context Rehydrator fuses three signals.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>Architecture</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">Everything runs locally.</h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {ARCH.map((a) => (
            <Item key={a.t} className="bg-ink p-7">
              <h3 className="font-serif text-lg text-bone">{a.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{a.d}</p>
            </Item>
          ))}
        </Stagger>
        <Reveal delay={0.1}>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <IngestionDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 2 — Deterministic ingestion pipeline.</p>
            </div>
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <MemoryGraphDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 3 — Local knowledge graph (people · projects · orgs).</p>
            </div>
          </div>
        </Reveal>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Get started</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-4xl">Install, ingest, ask.</h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
              First run downloads a ~90 MB embedding model — the only download UPII ever needs. Without a local model it
              falls back to retrieval plus a deterministic response, so it always answers.
            </p>
            <div className="mt-8 max-w-md">
              <CopyInstall variant="block" />
              <PypiStats className="mt-4" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <CodeBlock>{`# build memory from a directory (idempotent, content-addressed)
upii ingest ./notes --recursive

# semantic + attributed retrieval
upii search "satellite latency"

# cited answer over your corpus — offline, zero bytes leave the device
upii ask "what did we agree on latency?"

# ambient capture, consent-gated
upii watch ./inbox
upii inbox            # approve / reject captures

# render the local knowledge graph
upii knowledge --graph`}</CodeBlock>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="mx-auto max-w-3xl">
          <Eyebrow>Why open source</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
            The substrate should be inspectable. The enterprise layer is the business.
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-mist">
            A memory layer asks for the deepest trust software can ask for. You cannot earn that behind a black box — so
            the core is MIT-licensed and public. Anyone can read how ingestion works, run the evaluation harness, and
            verify that the corpus never leaves the device. The business is what enterprises need on top: the Membrane,
            governance, audit and support.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <CTAButton to="/research/ueii" variant="outline">
              UEII — the enterprise layer <ArrowRight className="h-4 w-4" />
            </CTAButton>
            <CTAButton to="/blog/why-we-open-sourced-the-core" variant="ghost">
              <span className="link-underline pb-0.5">Read DF-007 →</span>
            </CTAButton>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
